/**
 * 라커 화면의 로딩·에러·빈 상태.
 *
 * 지금까지 이 화면은 로딩이 "로딩중..." 한 줄이었고, **에러와 0칸은 아무것도 그리지 않아
 * 백지**였다. 서버가 죽으면 관리자는 라커가 하나도 없는 것으로 읽는다.
 *
 * 뼈대는 OpsBriefing 의 OpsStates 를, 빈 상태는 MemberlistPage 의 renderEmptyState 를
 * 따랐다. 두 화면이 이미 쓰는 모양이라 학습 비용이 없다.
 */

const BORDER = '#E3EAF3';

const Bone = ({ h, w, r = 10 }: { h: number; w?: number | string; r?: number }) => (
    <div
        style={{
            height: h,
            width: w ?? '100%',
            borderRadius: r,
            background: 'linear-gradient(90deg,#EEF1F6 25%,#F6F8FB 50%,#EEF1F6 75%)',
            backgroundSize: '200% 100%',
            animation: 'lockerShimmer 1.4s ease-in-out infinite',
        }}
    />
);

const Keyframes = () => (
    <style>{`@keyframes lockerShimmer{0%{background-position:200% 0}100%{background-position:-200% 0}}`}</style>
);

/**
 * 로딩 뼈대. 스피너 대신 **실제 그리드 모양**을 그린다.
 * 화면이 어떻게 채워질지 미리 알면 같은 대기 시간도 짧게 느껴지고, 레이아웃이 튀지 않는다.
 */
export const LockerLoading = ({ cardCount = 24 }: { cardCount?: number }) => (
    <div className="flex w-full flex-col" style={{ gap: 16 }}>
        <Keyframes />
        <Bone h={44} r={12} />
        <div
            style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
                gap: 12,
            }}
        >
            {Array.from({ length: cardCount }).map((_, index) => (
                <Bone key={index} h={100} r={10} />
            ))}
        </div>
        <span className="sr-only" role="status" aria-live="polite">
            라커 현황을 불러오는 중입니다
        </span>
    </div>
);

export const LockerTableLoading = ({ rowCount = 6 }: { rowCount?: number }) => (
    <div className="flex w-full flex-col" style={{ gap: 10 }}>
        <Keyframes />
        {Array.from({ length: rowCount }).map((_, index) => (
            <Bone key={index} h={48} r={10} />
        ))}
        <span className="sr-only" role="status" aria-live="polite">
            회수 기록을 불러오는 중입니다
        </span>
    </div>
);

/**
 * 에러 상태.
 * "오류가 발생했습니다"만 띄우면 관리자가 할 수 있는 게 없다. 다시 시도 버튼을 같이 준다.
 */
export const LockerError = ({
    title = '라커 정보를 불러오지 못했습니다',
    detail,
    onRetry,
}: {
    title?: string;
    detail?: string;
    onRetry?: () => void;
}) => (
    <div
        className="flex w-full flex-col items-center rounded-[16px]"
        style={{ background: '#FFFFFF', border: `1px solid ${BORDER}`, padding: '56px 24px' }}
        role="alert"
    >
        <span style={{ fontSize: 34 }} aria-hidden="true">
            🔌
        </span>
        <span style={{ fontSize: 18, fontWeight: 700, color: '#16202E', marginTop: 14 }}>{title}</span>
        <span style={{ fontSize: 14, color: '#7C8794', marginTop: 8, textAlign: 'center', lineHeight: '21px' }}>
            잠시 후 다시 시도해 주세요.
            <br />
            계속 안 되면 라포 고객센터로 알려주시면 바로 확인하겠습니다.
        </span>
        {detail && <span style={{ fontSize: 12, color: '#B4BDC9', marginTop: 10 }}>({detail})</span>}
        {onRetry && (
            <button
                type="button"
                onClick={onRetry}
                className="rounded-[10px]"
                style={{
                    marginTop: 18,
                    height: 44,
                    padding: '0 24px',
                    background: '#1A305D',
                    border: 'none',
                    color: '#FFFFFF',
                    fontSize: 15,
                    fontWeight: 700,
                    cursor: 'pointer',
                }}
            >
                다시 불러오기
            </button>
        )}
    </div>
);

/** 빈 상태. 무엇이 없는지가 아니라 **다음에 뭘 하면 되는지**를 말한다. */
export const LockerEmpty = ({
    icon = '🔐',
    title,
    description,
    actionLabel,
    onAction,
}: {
    icon?: string;
    title: string;
    description: string;
    actionLabel?: string;
    onAction?: () => void;
}) => (
    <div
        className="flex w-full flex-col items-center justify-center"
        style={{
            padding: '72px 24px',
            gap: 12,
            minHeight: 320,
            borderRadius: 20,
            border: '1px dashed #CBD5E1',
            background: 'linear-gradient(180deg, #FFFFFF 0%, #F8FAFC 100%)',
            textAlign: 'center',
        }}
    >
        <div style={{ fontSize: 44 }} aria-hidden="true">
            {icon}
        </div>
        <div style={{ fontSize: 22, fontWeight: 800, color: '#111827', letterSpacing: -0.5 }}>{title}</div>
        <div style={{ maxWidth: 460, fontSize: 15, lineHeight: 1.65, color: '#6B7280' }}>{description}</div>
        {actionLabel && onAction && (
            <button
                type="button"
                onClick={onAction}
                style={{
                    marginTop: 4,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: 44,
                    padding: '0 18px',
                    borderRadius: 12,
                    border: '1px solid #BFDBFE',
                    backgroundColor: '#EFF6FF',
                    color: '#1D4ED8',
                    fontSize: 14,
                    fontWeight: 700,
                    cursor: 'pointer',
                }}
            >
                {actionLabel}
            </button>
        )}
    </div>
);
